import { watch, onBeforeUnmount } from 'vue'

import { useDragScroll } from '@/composables/ui/useDragScroll'

const DRAG_THRESHOLD = 5

/**
 * 捲動容器的滑鼠拖曳捲動（含點擊攔截）
 * useDragScroll 不攔截點擊，拖曳放開時會誤觸容器內卡片的 click，
 * 因此在拖曳確實被 useDragScroll 接手時，記錄按住期間是否曾移動超過門檻，並在捕獲階段攔下該次 click。
 * 容器 ref 變動（v-if 重建）時會自動重新掛載。
 * @param {import('vue').Ref<HTMLElement|null>} containerRef - 捲動容器
 */
export function useDragScrollContainer(containerRef) {
  const dragScroll = useDragScroll()
  let el = null
  let startX = 0
  let startY = 0
  let hasDragged = false
  let isDragAccepted = false

  // 冒泡階段且註冊在 dragScroll.init 之後：此時本層與內層的 useDragScroll 都已處理完 mousedown，
  // 可依 dragScrollHandled 得知這次拖曳是否真的被接手（容器不可捲動時不接手，點擊就不該被攔）
  const handleMouseDown = (e) => {
    hasDragged = false
    isDragAccepted = e.button === 0 && !!e.dragScrollHandled
    startX = e.clientX
    startY = e.clientY
  }

  // 以按住期間的累計判斷，拖出去再拖回起點放開仍算拖曳
  const handleMouseMove = (e) => {
    if (!isDragAccepted || hasDragged || !(e.buttons & 1)) return
    if (Math.abs(e.clientX - startX) > DRAG_THRESHOLD || Math.abs(e.clientY - startY) > DRAG_THRESHOLD) {
      hasDragged = true
    }
  }

  const handleClickCapture = (e) => {
    // 鍵盤觸發的 click（detail 為 0）與拖曳無關，不可攔截
    if (e.detail === 0 || !hasDragged) return
    hasDragged = false
    e.stopPropagation()
    e.preventDefault()
  }

  // 圖片的原生拖曳會中斷 mousemove，導致捲動卡住
  const preventNativeDrag = (e) => e.preventDefault()

  const detach = () => {
    if (!el) return
    dragScroll.destroy()
    el.removeEventListener('mousedown', handleMouseDown)
    el.removeEventListener('mousemove', handleMouseMove, true)
    el.removeEventListener('click', handleClickCapture, true)
    el.removeEventListener('dragstart', preventNativeDrag)
    el = null
  }

  const attach = (target) => {
    el = target
    dragScroll.init(el)
    el.addEventListener('mousedown', handleMouseDown)
    el.addEventListener('mousemove', handleMouseMove, true)
    el.addEventListener('click', handleClickCapture, true)
    el.addEventListener('dragstart', preventNativeDrag)
  }

  watch(
    containerRef,
    (target) => {
      detach()
      if (target) attach(target)
    },
    { immediate: true, flush: 'post' }
  )

  onBeforeUnmount(detach)
}
